// Work Steps hooks. Landing/config-page hooks call the real /work-steps
// endpoints (mirrors useControlsV2's fetch/pagination pattern); the session
// ("Test") hooks call the /work-steps session endpoints.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError, fetchFastApi } from "../api/fastapi";
import {
  ListFetchMode,
  mergeListPage,
  nextListOffset,
} from "../lib/listPaging";
import {
  noOpsAudit,
  OpsAuditSelection,
} from "../models/audit/opsAuditSelection";
import { ConfiguredState } from "../models/audit/control";
import { OptroAccessListFlagSchema } from "../models/audit/optroAccess";
import { mergeOptroAccessUnavailable } from "../lib/optroAccessListFlag";
import { Procedure } from "../models/audit/auditSession";
import {
  OpsAudit,
  OpsAuditSchema,
  WorkStepAttribute,
  WorkStepFilterOptions,
  WorkStepFilterOptionsSchema,
  WorkStepSession,
  WorkStepSessionSchema,
  WorkStepWithAttributesSchema,
  WorkStepWithUser,
  WorkStepWithUserSchema,
} from "../models/audit/workStep";

const PAGE_LIMIT = 50;

/**
 * Wire form of a parent-audit selection. The API reads the two keys as
 * distinct intents — `ops_audit_id` picks an existing audit, `ops_audit_name`
 * asks for a NEW one under that title — because audit names are not unique
 * (SOX-108064) and so cannot select. Both null clears the link.
 */
const opsAuditBody = (
  selection: OpsAuditSelection,
): { ops_audit_id: string | null; ops_audit_name: string | null } => {
  switch (selection.kind) {
    case "existing":
      return { ops_audit_id: selection.id, ops_audit_name: null };
    case "new":
      return { ops_audit_id: null, ops_audit_name: selection.name };
    case "none":
      return { ops_audit_id: null, ops_audit_name: null };
  }
};

export interface WorkStepFilters {
  searchTerm?: string;
  configuredStates?: ConfiguredState[];
  sections?: string[];
  preparers?: string[];
  opsAuditIds?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const useWorkSteps = (filters: WorkStepFilters) => {
  const [workSteps, setWorkSteps] = useState<WorkStepWithUser[]>([]);
  const [totalWorkStepsCount, setTotalWorkStepsCount] = useState(0);
  // See OptroAccessListFlagSchema: linked rows are hidden from this list.
  // Held until the list resets (mergeOptroAccessUnavailable).
  const [optroAccessUnavailable, setOptroAccessUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const isLoadingRef = useRef(false);
  // A reset requested while a fetch is in flight is queued, not dropped:
  // the in-flight guard below would otherwise swallow the filter-change
  // reset (whose effect has already cleared `workSteps`) and `refetch()`.
  // Mirrors useControlsV2's pendingResetRef.
  const pendingResetRef = useRef(false);
  // Same for a first-page refresh; a queued reset supersedes it.
  const pendingRefreshRef = useRef(false);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchWorkSteps = useCallback(async (mode: ListFetchMode) => {
    if (isLoadingRef.current) {
      if (mode === "reset") {
        pendingResetRef.current = true;
      } else if (mode === "refresh") {
        pendingRefreshRef.current = true;
      }
      return;
    }

    isLoadingRef.current = true;
    const currentOffset = mode === "more" ? offsetRef.current : 0;
    const currentFilters = filtersRef.current;

    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        limit: PAGE_LIMIT.toString(),
        offset: currentOffset.toString(),
      });

      if (currentFilters.searchTerm) {
        queryParams.append("search", currentFilters.searchTerm);
      }

      if (currentFilters.sections && currentFilters.sections.length > 0) {
        currentFilters.sections.forEach((section) => {
          queryParams.append("sections", section);
        });
      }

      if (currentFilters.preparers && currentFilters.preparers.length > 0) {
        currentFilters.preparers.forEach((preparer) => {
          queryParams.append("preparers", preparer);
        });
      }

      if (
        currentFilters.configuredStates &&
        currentFilters.configuredStates.length > 0
      ) {
        currentFilters.configuredStates.forEach((state) => {
          queryParams.append("configured_states", state);
        });
      }

      if (currentFilters.opsAuditIds && currentFilters.opsAuditIds.length > 0) {
        currentFilters.opsAuditIds.forEach((opsAuditId) => {
          queryParams.append("ops_audit_ids", opsAuditId);
        });
      }

      if (currentFilters.sortBy) {
        queryParams.append("sort_by", currentFilters.sortBy);
      }

      if (currentFilters.sortOrder) {
        queryParams.append("sort_order", currentFilters.sortOrder);
      }

      const response = await fetchFastApi({
        path: `/work-steps/?${queryParams.toString()}`,
        method: "GET",
      });

      const parsed = z
        .object({
          workSteps: z.array(WorkStepWithUserSchema),
          totalCount: z.number(),
        })
        .merge(OptroAccessListFlagSchema)
        .parse(response);
      const data = parsed.workSteps;
      setTotalWorkStepsCount(parsed.totalCount);
      setOptroAccessUnavailable((current) =>
        mergeOptroAccessUnavailable(
          mode === "reset",
          current,
          parsed.optroAccessUnavailable,
        ),
      );

      setWorkSteps((prev) => mergeListPage(mode, prev, data));

      // A refresh past the first page leaves `hasMore` to the pages already
      // loaded.
      if (mode !== "refresh" || offsetRef.current <= PAGE_LIMIT) {
        setHasMore(data.length === PAGE_LIMIT);
      }

      offsetRef.current = nextListOffset(
        mode,
        offsetRef.current,
        currentOffset,
        PAGE_LIMIT,
      );
    } catch (error) {
      console.error("Failed to fetch work steps:", error);
      toast.error("Failed to fetch work steps");
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      if (pendingResetRef.current) {
        pendingResetRef.current = false;
        pendingRefreshRef.current = false;
        offsetRef.current = 0;
        setHasMore(true);
        void fetchWorkSteps("reset");
      } else if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        void fetchWorkSteps("refresh");
      }
    }
  }, []);

  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setWorkSteps([]);
    fetchWorkSteps("reset");
  }, [filtersKey, fetchWorkSteps]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    fetchWorkSteps("more");
  }, [hasMore, fetchWorkSteps]);

  const refetch = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    fetchWorkSteps("reset");
  }, [fetchWorkSteps]);

  // Reloads the first page in place, keeping later pages already scrolled
  // in — for picking up newly created rows without resetting the list.
  const refresh = useCallback(() => {
    void fetchWorkSteps("refresh");
  }, [fetchWorkSteps]);

  return {
    workSteps,
    totalWorkStepsCount,
    optroAccessUnavailable,
    loading,
    loadMore,
    hasMore,
    refetch,
    refresh,
  };
};

const DEFAULT_FILTER_OPTIONS: WorkStepFilterOptions = {
  sections: [],
  subsections: [],
  preparers: [],
  configuredStates: [],
  opsAudits: [],
};

export const useWorkStepFilterOptions = () => {
  const [filterOptions, setFilterOptions] = useState<WorkStepFilterOptions>(
    DEFAULT_FILTER_OPTIONS,
  );

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await fetchFastApi({
        path: "/work-steps/filter-options",
        method: "GET",
      });
      setFilterOptions(
        WorkStepFilterOptionsSchema.parse(response.filterOptions),
      );
    } catch (error) {
      console.error("Failed to fetch work step filter options:", error);
      toast.error("Failed to fetch work step filter options");
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  return { filterOptions };
};

export const useOpsAudits = () => {
  const [opsAudits, setOpsAudits] = useState<OpsAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Id of the newest request. Refetches can overlap (Work Steps reloads the
  // audits each time an import item lands), so only the newest request's
  // response is applied and only it clears `loading` — an older one landing
  // late can't overwrite fresher audits or report the list as settled.
  const requestIdRef = useRef(0);

  const fetchOpsAudits = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isNewest = () => requestId === requestIdRef.current;
    try {
      setLoading(true);
      setError(false);
      const response = await fetchFastApi({
        path: "/work-steps/ops-audits",
        method: "GET",
      });
      if (!isNewest()) return;
      setOpsAudits(z.array(OpsAuditSchema).parse(response.opsAudits));
    } catch (err) {
      if (!isNewest()) return;
      console.error("Failed to fetch ops audits:", err);
      toast.error("Failed to fetch ops audits");
      setError(true);
    } finally {
      if (isNewest()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpsAudits();
  }, [fetchOpsAudits]);

  return { opsAudits, loading, error, refetch: fetchOpsAudits };
};

// Hook for fetching a work step's sessions ("Tests")
export const useWorkStepSessions = (workStepId: string | null) => {
  const [sessions, setSessions] = useState<WorkStepSession[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!workStepId) {
      setSessions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/work-steps/work-step/${workStepId}/sessions`,
        method: "GET",
      });

      const data = z
        .array(WorkStepSessionSchema)
        .parse(response.sessions || []);
      setSessions(data);
    } catch (error) {
      console.error("Failed to fetch work step sessions:", error);
      toast.error("Failed to fetch work step sessions");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [workStepId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return { sessions, loading, refetch: fetchSessions };
};

// Hook for creating a work step session ("Test")
// Machine codes from the create-session endpoint's Optro strategy write,
// translated for the toast (the raw codes are API contract, not user copy).
const CREATE_SESSION_ERROR_MESSAGES: Record<string, string> = {
  // Status-frozen work steps never reach this toast (the New Test button is
  // already disabled with the status as its tooltip), so this is the
  // permission-denial message.
  optro_item_not_editable:
    "You do not have permission in Optro to create a new test for this Work Step.",
  optro_unreachable:
    "Couldn't reach Optro to update this Work Step's testing strategy. Try again in a moment.",
  optro_link_unavailable:
    "This Work Step's Optro link isn't usable right now. Check the Optro integration connection.",
};

export const useCreateWorkStepSession = () => {
  const [loading, setLoading] = useState(false);

  const createSession = useCallback(
    async (workStepId: string, name: string): Promise<string | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/work-steps/work-step/${workStepId}/create-session`,
          method: "POST",
          throwOnErrorEnvelope: true,
          body: {
            session_name: name,
          },
        });

        toast.success("Test created successfully");
        return response.session_id;
      } catch (error) {
        console.error("Failed to create work step session:", error);
        // Mirror controls' createSession: surface the API detail (e.g.
        // "Work step has no testing tables") instead of a generic toast -
        // but translate the machine codes the Optro strategy write returns.
        if (error instanceof ApiError) {
          toast.error(
            CREATE_SESSION_ERROR_MESSAGES[error.detail ?? ""] ??
              error.getUserMessage(),
          );
        } else {
          toast.error("Failed to create test");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createSession, loading };
};

// Hook for deleting a work step session ("Test")
export const useDeleteWorkStepSession = () => {
  const [loading, setLoading] = useState(false);

  const deleteSession = useCallback(
    async (sessionId: string): Promise<void> => {
      setLoading(true);
      try {
        await fetchFastApi({
          path: `/work-steps/work-step-sessions/${sessionId}`,
          method: "DELETE",
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { deleteSession, loading };
};

// --- Configuration-page hooks (wayfinder ticket 06) ---

export const useWorkStep = (id: string | null) => {
  const [workStep, setWorkStep] = useState<WorkStepWithUser | null>(null);
  const [attributes, setAttributes] = useState<WorkStepAttribute[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWorkStep = useCallback(
    async ({ backgroundFetch = false }: { backgroundFetch?: boolean } = {}) => {
      if (!id) {
        setWorkStep(null);
        setAttributes([]);
        setProcedures([]);
        setLoading(false);
        return;
      }
      // Mirrors useControlsV2's fetchControlWithProcedures: a background
      // fetch (e.g. after an attribute-only save) updates state in place
      // without flipping `loading`, so the page doesn't blank to a
      // full-screen spinner for what's otherwise an in-place refresh.
      if (!backgroundFetch) {
        setLoading(true);
      }
      try {
        const response = await fetchFastApi({
          path: `/work-steps/work-step/${id}`,
          method: "GET",
        });
        const {
          attributes: parsedAttributes,
          procedures: parsedProcedures,
          ...rest
        } = WorkStepWithAttributesSchema.parse(response);
        setWorkStep(rest);
        setAttributes(parsedAttributes);
        setProcedures(parsedProcedures);
      } catch (error) {
        console.error("Failed to fetch work step:", error);
        toast.error("Failed to fetch work step");
        setWorkStep(null);
        setAttributes([]);
        setProcedures([]);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchWorkStep({ backgroundFetch: false });
  }, [fetchWorkStep]);

  return {
    workStep,
    attributes,
    procedures,
    loading,
    refetch: fetchWorkStep,
  };
};

export interface CreateWorkStepInput {
  name: string;
  description: string;
  section?: string;
  subsection?: string;
  preparer?: string;
  /** Parent audit, as the two distinct intents the API reads. See `opsAuditBody`. */
  opsAudit?: OpsAuditSelection;
}

export const useCreateWorkStep = () => {
  const [loading, setLoading] = useState(false);

  const createWorkStep = useCallback(
    async (input: CreateWorkStepInput): Promise<WorkStepWithUser> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/work-steps/",
          method: "POST",
          body: {
            name: input.name,
            description: input.description,
            section: input.section ?? null,
            subsection: input.subsection ?? null,
            preparer: input.preparer ?? null,
            ...opsAuditBody(input.opsAudit ?? noOpsAudit),
          },
        });
        const result = WorkStepWithUserSchema.parse(response);
        toast.success("Work step created successfully");
        return result;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createWorkStep, loading };
};

export interface BulkImportWorkStepRow {
  name: string;
  description: string;
  section?: string;
  subsection?: string;
  preparer?: string;
  opsAuditName?: string;
}

const BulkImportWorkStepsResponseSchema = z.object({
  message: z.string(),
  totalCount: z.number(),
});

export const useBulkImportWorkSteps = () => {
  const [loading, setLoading] = useState(false);

  const bulkImportWorkSteps = useCallback(
    async (rows: BulkImportWorkStepRow[]): Promise<number> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: "/work-steps/bulk-import-work-steps",
          method: "POST",
          body: {
            work_steps: rows.map((row) => ({
              name: row.name,
              description: row.description,
              section: row.section ?? null,
              subsection: row.subsection ?? null,
              preparer: row.preparer ?? null,
              ops_audit_name: row.opsAuditName ?? null,
            })),
          },
        });
        return BulkImportWorkStepsResponseSchema.parse(response).totalCount;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { bulkImportWorkSteps, loading };
};

export interface WorkStepUpdate {
  name: string;
  description: string;
  section: string | null;
  subsection: string | null;
  preparer: string | null;
  configuredState: ConfiguredState;
  // CRITICAL: always send the current selection — a `{ kind: "none" }` here
  // clears the work step's audit link.
  opsAudit: OpsAuditSelection;
}

export const useUpdateWorkStep = () => {
  const [loading, setLoading] = useState(false);

  const updateWorkStep = useCallback(
    async (id: string, updates: WorkStepUpdate): Promise<WorkStepWithUser> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/work-steps/work-step/${id}`,
          method: "PUT",
          body: {
            name: updates.name,
            description: updates.description,
            section: updates.section ?? null,
            subsection: updates.subsection ?? null,
            preparer: updates.preparer ?? null,
            configured_state: updates.configuredState,
            ...opsAuditBody(updates.opsAudit),
          },
        });
        return WorkStepWithUserSchema.parse(response);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateWorkStep, loading };
};

export const useDeleteWorkStep = () => {
  const [loading, setLoading] = useState(false);

  const deleteWorkStep = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    try {
      await fetchFastApi({
        path: `/work-steps/work-step/${id}`,
        method: "DELETE",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteWorkStep, loading };
};

export const useDuplicateWorkStep = () => {
  const [loading, setLoading] = useState(false);

  const duplicateWorkStep = useCallback(
    async (id: string): Promise<WorkStepWithUser> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/work-steps/work-step/${id}/duplicate`,
          method: "POST",
        });
        return WorkStepWithUserSchema.parse(response);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { duplicateWorkStep, loading };
};

const OverrideWorkStepParentResponseSchema = z.union([
  z.object({ approvalRequestId: z.string() }),
  // Only the new template's id is read: the page navigates to it, and the
  // template page loads the full work step itself.
  z.object({ workStep: z.object({ id: z.string() }) }),
]);

export type OverrideWorkStepParentResult =
  | { kind: "pending_approval" }
  | { kind: "overridden"; newTemplateId: string };

// Replaces a work step template with one of its test copies, as Override
// Parent Control does for controls. A 202 means the change waits for approval.
export const useOverrideWorkStepParent = () => {
  const [loading, setLoading] = useState(false);

  const overrideParent = useCallback(
    async (
      parentWorkStepId: string,
      childWorkStepId: string,
    ): Promise<OverrideWorkStepParentResult> => {
      setLoading(true);
      try {
        const response = OverrideWorkStepParentResponseSchema.parse(
          await fetchFastApi({
            path: `/work-steps/work-step/${parentWorkStepId}/override-parent-with-child`,
            method: "POST",
            body: { child_work_step_id: childWorkStepId },
          }),
        );
        return "approvalRequestId" in response
          ? { kind: "pending_approval" }
          : { kind: "overridden", newTemplateId: response.workStep.id };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { overrideParent, loading };
};

export const useWorkStepAttributeMutations = (workStepId: string | null) => {
  const createAttribute = useCallback(
    async (attribute: {
      reference: string;
      statement: string;
      description: string;
    }): Promise<void> => {
      if (!workStepId) return;
      await fetchFastApi({
        path: `/work-steps/work-step/${workStepId}/attributes`,
        method: "POST",
        body: attribute,
      });
    },
    [workStepId],
  );

  const updateAttribute = useCallback(
    async (
      attributeId: string,
      updates: Partial<{
        reference: string;
        statement: string;
        description: string;
      }>,
    ): Promise<void> => {
      if (!workStepId) return;
      await fetchFastApi({
        path: `/work-steps/work-step/${workStepId}/attributes/${attributeId}`,
        method: "PUT",
        body: {
          // The backend requires all three fields on every PUT; callers in
          // this codebase always pass the full draft, but the empty-string
          // fallback keeps the type honest for the `Partial<>` signature.
          reference: updates.reference ?? "",
          statement: updates.statement ?? "",
          description: updates.description ?? "",
        },
      });
    },
    [workStepId],
  );

  const deleteAttribute = useCallback(
    async (attributeId: string): Promise<void> => {
      if (!workStepId) return;
      await fetchFastApi({
        path: `/work-steps/work-step/${workStepId}/attributes/${attributeId}`,
        method: "DELETE",
      });
    },
    [workStepId],
  );

  return { createAttribute, updateAttribute, deleteAttribute };
};
